import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { postReq } from "../api.js";
import moment from "moment";

const statusColor = (status) => {
  switch (status) {
    case "APPROVED":
      return "success";
    case "REJECTED":
      return "error";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
};

const TeamCorrectionTable = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 1,
    search: "",
  });
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' | 'reject'
  const [actionLoading, setActionLoading] = useState(false);

  const fetchCorrections = async () => {
    setLoading(true);
    try {
      const res = await postReq("user/teamCorrections", {
        page: paginationModel.page,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
      });
      setRows(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      message.error(err?.message || "Error fetching corrections");
    } finally {
      setLoading(false);
    }
  };

  const updateCorrection = async (id, status) => {
    try {
      const res = await postReq(`user/approveCorrection/${id}`, { status });
      if (res.status === 200) {
        message.success(
          res.message ||
            `Correction ${status === "APPROVED" ? "Approved" : "Rejected"}`,
        );
        fetchCorrections();
      } else {
        message.error(res.message || "Something went wrong");
      }
    } catch (error) {
      message.error(error.message || "Something went wrong");
    }
  };

  const handleConfirmAction = async () => {
    if (!selectedId) return;
    setActionLoading(true);
    await updateCorrection(
      selectedId,
      actionType === "approve" ? "APPROVED" : "REJECTED",
    );
    setActionLoading(false);
    closeConfirm();
  };

  const closeConfirm = () => {
    setOpenConfirm(false);
    setSelectedId(null);
    setActionType(null);
  };

  const openAction = (id, type) => {
    setSelectedId(id);
    setActionType(type);
    setOpenConfirm(true);
  };

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, search, page: 1 }));
    }, 600);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchCorrections();
  }, [paginationModel]);

  const columns = useMemo(
    () => [
      {
        title: "Employee",
        key: "employee",
        width: 180,
        render: (_, row) =>
          `${row.user?.firstName ?? ""} ${row.user?.lastName ?? ""}`.trim() ||
          "-",
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 120,
        render: (val) => moment(val).format("DD/MM/YYYY"),
      },
      {
        title: "Attendance Time",
        key: "attendanceTime",
        width: 200,
        render: (_, row) =>
          `${moment(row.attendance?.loginTime).format("HH:mm A")} - ${moment(row.attendance?.logoutTime).format("HH:mm A")}`,
      },
      {
        title: "Requested Time",
        key: "requestedTime",
        width: 200,
        render: (_, row) =>
          `${moment(row.requestedLogin).format("HH:mm A")} - ${moment(row.requestedLogout).format("HH:mm A")}`,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status) => (
          <Tag color={statusColor(status)} style={{ fontWeight: 500 }}>
            {status}
          </Tag>
        ),
      },
      {
        title: "Updated On",
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: 160,
        render: (val) => moment(val).format("DD/MM/YYYY HH:mm"),
      },
      {
        title: "Action",
        key: "action",
        width: 100,
        render: (_, row) => (
          <Space size={4}>
            <Button
              type="text"
              disabled={row.status !== "PENDING"}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              icon={
                <CheckOutlined
                  style={{
                    color: row.status === "PENDING" ? "#52c41a" : undefined,
                    fontSize: 16,
                  }}
                />
              }
              onClick={() => openAction(row._id, "approve")}
            />
            <Button
              type="text"
              disabled={row.status !== "PENDING"}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              icon={
                <CloseOutlined
                  style={{
                    color: row.status === "PENDING" ? "#ff4d4f" : undefined,
                  }}
                />
              }
              onClick={() => openAction(row._id, "reject")}
            />
          </Space>
        ),
      },
    ],
    [],
  );

  const isApprove = actionType === "approve";

  return (
    <Card
      bordered={false}
      styles={{ body: { padding: "24px 40px" } }}
      style={{ flex: 1 }}
    >
      <Input
        placeholder="Search Team Member"
        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 240, marginBottom: 24 }}
      />

      <Table
        loading={loading}
        dataSource={rows}
        columns={columns}
        rowKey={(row) => row._id}
        scroll={{ x: "max-content" }}
        pagination={{
          current: paginationModel.page,
          pageSize: paginationModel.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20"],
          showTotal: (tot) => `Total ${tot} records`,
          onChange: (page, pageSize) =>
            setPaginationModel((prev) => ({ ...prev, page, pageSize })),
        }}
      />

      <Modal
        open={openConfirm}
        onCancel={closeConfirm}
        title={isApprove ? "Approve Correction" : "Reject Correction"}
        footer={[
          <Button key="cancel" onClick={closeConfirm}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger={!isApprove}
            style={
              isApprove
                ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
                : {}
            }
            loading={actionLoading}
            onClick={handleConfirmAction}
          >
            {isApprove ? "Approve" : "Reject"}
          </Button>,
        ]}
      >
        <Typography.Text>
          Are you sure you want to {actionType} this correction?
        </Typography.Text>
      </Modal>
    </Card>
  );
};

export default TeamCorrectionTable;
